import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, GraduationCap, Music2, Sparkles, Trash2 } from "lucide-react";
import { api, ASSETS, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import OrnamentalDivider from "../components/site/OrnamentalDivider";

export default function Dashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [courses, setCourses] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [loadingEnroll, setLoadingEnroll] = useState(null);

    useEffect(() => {
        Promise.all([api.get("/courses"), api.get("/enrollments")])
            .then(([c, e]) => {
                setCourses(c.data || []);
                setEnrollments(e.data || []);
            })
            .catch(() => {});
    }, []);

    const enrolledIds = new Set(enrollments.map((e) => e.course_id));

    const handleEnroll = async (course) => {
        setLoadingEnroll(course.id);
        try {
            const { data } = await api.post("/enrollments", {
                course_id: course.id,
                mode: "online",
            });
            setEnrollments((prev) =>
                prev.find((p) => p.course_id === data.course_id) ? prev : [...prev, data]
            );
            toast.success(`Enrolled in ${course.title}. Aacharya will reach out shortly.`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not enroll.");
        } finally {
            setLoadingEnroll(null);
        }
    };

    const handleCancel = async (enr) => {
        try {
            await api.delete(`/enrollments/${enr.enrollment_id}`);
            setEnrollments((prev) => prev.filter((p) => p.enrollment_id !== enr.enrollment_id));
            toast("Enrollment cancelled.");
        } catch (err) {
            toast.error("Could not cancel.");
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    const u = typeof user === "object" && user ? user : null;

    return (
        <div data-testid="dashboard-page" className="min-h-screen bg-ivory">
            <header className="border-b border-gold-100 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={ASSETS.logo} alt="Logo" className="h-12 w-12 rounded-full ring-1 ring-gold/40" />
                        <div className="leading-tight">
                            <div className="font-display text-base text-maroon font-semibold">
                                Shri Vishwa Bandhu
                            </div>
                            <div className="eyebrow text-[10px] text-gold">Shishya Dashboard</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4">
                        <div className="hidden sm:flex items-center gap-3">
                            {u?.picture ? (
                                <img src={u.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
                            ) : (
                                <div className="h-9 w-9 rounded-full bg-maroon text-gold-300 flex items-center justify-center font-display">
                                    {(u?.name || "S").slice(0, 1).toUpperCase()}
                                </div>
                            )}
                            <div className="leading-tight">
                                <div className="font-display text-maroon" data-testid="dashboard-user-name">{u?.name}</div>
                                <div className="text-[11px] uppercase tracking-[0.2em] text-inkmuted">
                                    {u?.email}
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            data-testid="logout-button"
                            className="btn-ghost-gold inline-flex items-center gap-2"
                        >
                            <LogOut size={14} />
                            Logout
                        </button>
                        {u?.role === "admin" && (
                            <Link
                                to="/admin"
                                data-testid="dashboard-admin-link"
                                className="btn-primary inline-flex items-center gap-2"
                            >
                                Admin
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <section className="lg:col-span-8">
                        <div className="eyebrow">· Namaste, Shishya ·</div>
                        <h1 className="font-display text-4xl sm:text-5xl text-maroon mt-3 leading-tight">
                            Welcome,{" "}
                            <span className="italic text-gold" data-testid="dashboard-greeting-name">
                                {u?.name?.split(" ")[0] || "Friend"}
                            </span>
                        </h1>
                        <p className="font-serif text-ink/80 mt-4 max-w-2xl leading-relaxed">
                            “Music is a discipline that shapes the mind. Practice silent. Perform divine.”
                        </p>

                        <OrnamentalDivider className="my-10" />

                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-display text-2xl text-maroon">
                                <GraduationCap className="inline w-5 h-5 mb-1 mr-2 text-gold" />
                                My Enrollments
                            </h2>
                            <span className="eyebrow text-gold" data-testid="dashboard-enrollment-count">
                                {enrollments.length} enrolled
                            </span>
                        </div>

                        {enrollments.length === 0 ? (
                            <div
                                data-testid="dashboard-no-enrollments"
                                className="card-cream p-8 text-center"
                            >
                                <Sparkles className="mx-auto text-gold mb-3" size={22} />
                                <p className="font-display text-xl text-maroon">
                                    You haven’t enrolled in any course yet.
                                </p>
                                <p className="font-serif text-ink/70 mt-2">
                                    Browse the offerings below and begin your sadhana.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {enrollments.map((e) => (
                                    <div
                                        key={e.enrollment_id}
                                        data-testid={`enrollment-row-${e.course_id}`}
                                        className="card-cream p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                                    >
                                        <div>
                                            <div className="font-display text-xl text-maroon">
                                                {e.course_title}
                                            </div>
                                            <div className="eyebrow text-gold mt-1">
                                                · {e.mode} · {e.status} ·
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleCancel(e)}
                                            data-testid={`cancel-enrollment-${e.course_id}`}
                                            className="inline-flex items-center gap-2 text-maroon hover:text-maroon-700 text-sm font-sans tracking-[0.2em] uppercase"
                                        >
                                            <Trash2 size={14} /> Cancel
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <h2 className="font-display text-2xl text-maroon mt-14 mb-6">
                            <Music2 className="inline w-5 h-5 mb-1 mr-2 text-gold" />
                            Explore Courses
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {courses.map((c) => {
                                const enrolled = enrolledIds.has(c.id);
                                return (
                                    <div
                                        key={c.id}
                                        className="card-cream p-6 flex flex-col"
                                        data-testid={`dashboard-course-${c.id}`}
                                    >
                                        <div className="font-display text-xl text-maroon">
                                            {c.title}
                                        </div>
                                        <p className="font-serif text-ink/75 text-sm mt-2 flex-1 leading-relaxed">
                                            {c.summary}
                                        </p>
                                        <div className="mt-4 pt-3 border-t border-gold-100 flex items-center justify-between">
                                            <span className="eyebrow text-gold">{c.level}</span>
                                            <button
                                                onClick={() => !enrolled && handleEnroll(c)}
                                                disabled={enrolled || loadingEnroll === c.id}
                                                data-testid={`enroll-button-${c.id}`}
                                                className={
                                                    enrolled
                                                        ? "text-[12px] uppercase tracking-[0.22em] font-sans text-gold cursor-default"
                                                        : "text-[12px] uppercase tracking-[0.22em] font-sans text-maroon hover:text-gold transition-colors"
                                                }
                                            >
                                                {enrolled
                                                    ? "✓ Enrolled"
                                                    : loadingEnroll === c.id
                                                    ? "Enrolling…"
                                                    : "Enroll →"}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    <aside className="lg:col-span-4 space-y-6">
                        <div className="card-cream p-7">
                            <div className="eyebrow text-gold">· Profile ·</div>
                            <h3 className="font-display text-2xl text-maroon mt-3">{u?.name}</h3>
                            <p className="font-serif text-ink/75 mt-1">{u?.email}</p>
                            <div className="mt-5 pt-5 border-t border-gold-100 grid grid-cols-2 gap-3 text-sm font-serif">
                                <div>
                                    <div className="eyebrow text-ink/60">Provider</div>
                                    <div className="text-maroon capitalize" data-testid="dashboard-provider">
                                        {u?.provider}
                                    </div>
                                </div>
                                <div>
                                    <div className="eyebrow text-ink/60">Role</div>
                                    <div className="text-maroon capitalize">{u?.role}</div>
                                </div>
                            </div>
                        </div>

                        <div className="p-7 bg-maroon text-ivory">
                            <div className="eyebrow text-gold-300">· A Thought ·</div>
                            <p className="font-display italic text-2xl mt-3 leading-snug">
                                “Where music flourishes, humanity blossoms.”
                            </p>
                            <p className="text-kannada text-gold-300 mt-4 leading-relaxed">
                                ಸಂಗೀತ ಬೆಳಗಿದರೆ, ಮಾನವತೆಯೂ ಅರಳುತ್ತದೆ.
                            </p>
                        </div>

                        <Link
                            to="/#contact"
                            data-testid="dashboard-contact-link"
                            className="btn-ghost-gold w-full block text-center"
                        >
                            Talk to the Gurukula
                        </Link>
                    </aside>
                </div>
            </main>
        </div>
    );
}
